import React, { useEffect, useState } from "react";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Plus, Calendar, MapPin, Users, TrendingUp, ChevronRight, Building2, School, Sparkles, Trash2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader, StatCard } from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";
import { useSubmitting } from "@/lib/useSubmitting";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from "recharts";

const WORKFLOW_STEP_LABELS = {
  proposal: "Proposal", budget: "Budget", secretariat_form: "Secretariat Form",
  registration_school: "Reg. (School)", registration_individual: "Reg. (Individual)",
  committees_agendas: "Committees & Agendas", vendors: "Vendors",
  trophies: "Trophies", collaborations: "Collaborations",
  invitation_brochure: "Invitation", sponsorship_brochure: "Sponsorship",
  social_media_calendar: "Social Calendar", cash_prizes: "Cash Prizes",
  attendance_sheet: "Attendance", marking_sheet: "Marking",
  art_craft: "Art & Craft", merchandise_goodies: "Merchandise"
};

export default function PMDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [form, setForm] = useState({ name: "", address: "", email: "", school_name: "", school_email: "", start_date: "", end_date: "", logo_url: "", expected_delegates: 200, delegate_fee: 2500 });
  const eventSubmit = useSubmitting();

  const load = async () => {
    const [e, s] = await Promise.all([api.get("/events"), api.get("/dashboard/stats")]);
    setEvents(e.data);
    setStats(s.data);
  };
  useEffect(() => { load(); }, []);

  const createEvent = (ev) => {
    ev.preventDefault();
    return eventSubmit.run(async () => {
      try {
        // Strip blank optional fields so the API doesn't fail EmailStr validation on "" values
        const payload = { ...form };
        Object.keys(payload).forEach((k) => {
          if (payload[k] === "" || payload[k] === undefined) delete payload[k];
        });
        const r = await api.post("/events", payload);
        toast.success("Event created!");
        setShowCreate(false);
        load();
        nav(`/dashboard/events/${r.data.event_id}`);
      } catch (e2) { toast.error(e2?.response?.data?.detail || "Failed"); }
    });
  };

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/upload", fd);
    setForm({ ...form, logo_url: r.data.url });
    toast.success("Logo uploaded");
  };

  const confirmDeleteEvent = async () => {
    if (!deleteTarget) return;
    if (confirmText.trim() !== deleteTarget.name) {
      toast.error("Type the event name exactly to confirm");
      return;
    }
    setDeleteBusy(true);
    try {
      const r = await api.delete(`/events/${deleteTarget.event_id}`);
      const c = r?.data?.deleted_counts || {};
      const items = Object.entries(c).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(", ");
      toast.success(`"${deleteTarget.name}" deleted${items ? ` (${items})` : ""}`);
      setDeleteTarget(null);
      setConfirmText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleteBusy(false);
    }
  };

  const eventProgress = (e) => {
    const ws = e.workflow_status || {};
    const total = Object.keys(WORKFLOW_STEP_LABELS).length;
    const done = Object.values(ws).filter(Boolean).length;
    return Math.round((done / total) * 100);
  };

  // Completed events are archived by Admin — hidden from the main dashboard
  const activeEvents = events.filter((e) => !e.completed);
  const completedEvents = events.filter((e) => e.completed);

  // Chart data (active events only)
  const progressBuckets = [
    { name: "0-25%", count: activeEvents.filter((e) => eventProgress(e) <= 25).length },
    { name: "26-50%", count: activeEvents.filter((e) => eventProgress(e) > 25 && eventProgress(e) <= 50).length },
    { name: "51-75%", count: activeEvents.filter((e) => eventProgress(e) > 50 && eventProgress(e) <= 75).length },
    { name: "76-100%", count: activeEvents.filter((e) => eventProgress(e) > 75).length },
  ];

  return (
    <Layout navItems={getNav("project_manager")}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <PageHeader
          title={`Hello, ${user.name.split(" ")[0]} 👋`}
          subtitle={`Project Manager${user.city ? ` · ${user.city}` : ""} — your events at a glance`}
          icon={Sparkles}
          actions={<button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="pm-create-event-btn"><Plus className="w-4 h-4" /> New Event</button>} />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Events" value={stats?.total_events || 0} icon={Calendar} color="orange" />
          <StatCard label="Avg Progress" value={`${stats?.avg_workflow_progress || 0}%`} icon={TrendingUp} color="green" />
          <StatCard label="Completed" value={stats?.completed_events || 0} icon={Building2} color="indigo" />
          <StatCard label="Upcoming" value={stats?.upcoming_events || 0} icon={School} color="pink" />
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <div className="dashboard-card p-6 lg:col-span-2">
            <h3 className="font-display font-bold text-lg mb-4">Event Progress Distribution</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={progressBuckets}>
                <XAxis dataKey="name" axisLine={false} tickLine={false} style={{ fontSize: 12 }} />
                <YAxis axisLine={false} tickLine={false} style={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#F97316" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="dashboard-card p-6 bg-gradient-to-br from-orange-50 to-amber-50">
            <h3 className="font-display font-bold text-lg mb-3">Quick Tip</h3>
            <p className="text-sm text-slate-700 leading-relaxed">Complete each workflow step in order — locked portfolios appear faded in your allotment portal. Use the QR scanner at check-in to mark delegates present.</p>
          </div>
        </div>

        {/* Events Grid */}
        <h3 className="font-display font-bold text-xl mb-4">My Events</h3>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {activeEvents.length === 0 && (
            <div className="col-span-full dashboard-card p-12 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">{completedEvents.length ? "All your events are completed — see below." : "No events yet. Create your first event!"}</p>
            </div>
          )}
          {activeEvents.map((e) => (
            <motion.div key={e.event_id} whileHover={{ y: -4 }}
              className="dashboard-card p-5 relative group" data-testid={`event-card-${e.event_id}`}>
              <button
                onClick={(ev) => { ev.stopPropagation(); setDeleteTarget(e); setConfirmText(""); }}
                title="Delete event"
                aria-label="Delete event"
                className="absolute top-3 right-3 p-1.5 rounded-lg bg-white/95 text-rose-500 opacity-0 group-hover:opacity-100 hover:bg-rose-50 transition shadow"
                data-testid={`event-delete-${e.event_id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <div onClick={() => nav(`/dashboard/events/${e.event_id}`)} className="cursor-pointer">
                <div className="flex items-start gap-3">
                  {e.logo_url ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${e.logo_url}`} alt="" className="w-14 h-14 rounded-xl object-cover bg-slate-100" />
                  ) : (
                    <div className="w-14 h-14 rounded-xl brand-gradient flex items-center justify-center text-white font-display font-black text-lg">{e.name.charAt(0)}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-lg text-slate-900 truncate">{e.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Building2 className="w-3 h-3" /> {e.school_name}</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300" />
                </div>
                <div className="mt-4 flex justify-between items-center text-xs text-slate-500">
                  <div className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {e.start_date} → {e.end_date}</div>
                  <div className="font-bold text-orange-600">{eventProgress(e)}%</div>
                </div>
                <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full brand-gradient transition-all" style={{ width: `${eventProgress(e)}%` }} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Completed Events — archived here when an Admin marks the event complete */}
        {completedEvents.length > 0 && (
          <div className="mt-10" data-testid="pm-completed-events">
            <h3 className="font-display font-bold text-xl mb-1 text-slate-600">Completed Events ({completedEvents.length})</h3>
            <p className="text-xs text-slate-400 mb-4">Marked completed by Admin. Everything inside stays fully accessible and editable.</p>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
              {completedEvents.map((e) => (
                <motion.div key={e.event_id} whileHover={{ y: -4 }} onClick={() => nav(`/dashboard/events/${e.event_id}`)}
                  className="dashboard-card p-5 cursor-pointer border-l-4 border-emerald-400 opacity-90 hover:opacity-100 transition-opacity"
                  data-testid={`pm-completed-event-${e.event_id}`}>
                  <div className="flex items-start gap-3">
                    {e.logo_url ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${e.logo_url}`} alt="" className="w-14 h-14 rounded-xl object-cover bg-slate-100" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-emerald-500 flex items-center justify-center text-white font-display font-black text-lg">{e.name.charAt(0)}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-lg text-slate-900 truncate">{e.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Building2 className="w-3 h-3" /> {e.school_name}</div>
                    </div>
                    <span className="text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 shrink-0">Completed</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> {e.start_date} → {e.end_date}</div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto relative">
            <ModalCloseBtn onClose={() => setShowCreate(false)} />
            <h3 className="font-display font-black text-2xl mb-1">New Event</h3>
            <p className="text-sm text-slate-500 mb-4">Fill in basic details to get started.</p>
            <form onSubmit={createEvent} className="space-y-3">
              <input required placeholder="Event name (e.g., AHLCON MUN 2026)" className="input-pill" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ev-name" />
              <input required placeholder="Address" className="input-pill" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="ev-address" />
              <input required type="email" placeholder="Event email" className="input-pill" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ev-email" />
              <input required placeholder="School name" className="input-pill" value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} data-testid="ev-school" />
              <input type="email" placeholder="School email (for principal dashboard)" className="input-pill" value={form.school_email} onChange={(e) => setForm({ ...form, school_email: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-500">Start date</label><input required type="date" className="input-pill" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="ev-start" /></div>
                <div><label className="text-xs text-slate-500">End date</label><input required type="date" className="input-pill" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="ev-end" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-500">Expected delegates</label><input type="number" min={1} className="input-pill" value={form.expected_delegates} onChange={(e) => setForm({ ...form, expected_delegates: parseInt(e.target.value) || 0 })} /></div>
                <div><label className="text-xs text-slate-500">Delegate fee (₹)</label><input type="number" min={0} className="input-pill" value={form.delegate_fee} onChange={(e) => setForm({ ...form, delegate_fee: parseFloat(e.target.value) || 0 })} /></div>
              </div>
              <div>
                <label className="text-xs text-slate-500">Event Logo</label>
                <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} className="block w-full text-sm mt-1" />
                {form.logo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`} alt="logo" className="w-16 h-16 rounded-xl object-cover mt-2" />}
              </div>
              <button type="submit" disabled={eventSubmit.submitting} className="btn-primary w-full py-3 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="ev-submit">
                {eventSubmit.submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating event…</> : "Create event"}
              </button>
            </form>
          </motion.div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="delete-event-modal">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-2xl p-6 max-w-md w-full relative">
            <ModalCloseBtn onClose={() => { setDeleteTarget(null); setConfirmText(""); }} />
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center"><AlertTriangle className="w-6 h-6" /></div>
              <div>
                <h3 className="font-display font-black text-xl text-slate-900">Delete event?</h3>
                <p className="text-xs text-slate-500">This cannot be undone</p>
              </div>
            </div>
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 mb-3 text-xs text-rose-900 space-y-1">
              <div><b>{deleteTarget.name}</b></div>
              <div>Permanently removes: committees, allotments, budget, vendors, chat threads, principal assignments, social posts.</div>
            </div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Type <code className="bg-slate-100 px-1.5 py-0.5 rounded font-mono">{deleteTarget.name}</code> to confirm
            </label>
            <input
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={deleteTarget.name}
              className="input-pill mb-3"
              data-testid="delete-event-confirm-input"
            />
            <div className="flex gap-2">
              <button onClick={() => { setDeleteTarget(null); setConfirmText(""); }} className="btn-ghost flex-1 py-2.5" data-testid="delete-event-cancel-btn">
                Cancel
              </button>
              <button
                onClick={confirmDeleteEvent}
                disabled={deleteBusy || confirmText.trim() !== deleteTarget.name}
                className="flex-1 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold disabled:bg-slate-200 disabled:text-slate-400 transition flex items-center justify-center gap-2"
                data-testid="delete-event-submit-btn"
              >
                <Trash2 className="w-4 h-4" /> {deleteBusy ? "Deleting…" : "Delete forever"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </Layout>
  );
}
